/**
 * Trader-native bot spawn, embedded in the Value Dashboard trade ticket.
 *
 * BotModeSwitch wraps the manual order form with a mode-row
 * [Manual | Vanilla | Recycler]. It renders the row ONLY when the trader has bot
 * access AND there's a Kalshi market to spawn on — otherwise the children (the
 * existing order form) render byte-for-byte unchanged. Default mode is Manual,
 * so the existing flow is untouched until a trader opts in.
 *
 * A bot always deploys in GUARD mode (quote_for_rebate) — presence that avoids
 * fills. Letting it take fills ("let it ride") is a deliberate runtime toggle on
 * a claimed bot, in the Position Tracker.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Bot, ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { useAuthStore } from '@/stores/authStore';
import { useBotAccess } from '@/hooks/useBotAccess';
import {
  kindForMode,
  sizeFieldForKind,
  resolveBotConfig,
  spawnBot,
  claimBot,
  type TraderBotMode,
} from '@/lib/bots/edge';
import type { BotKindConfig } from '@/lib/bots/types';
import { specForKind } from '@/lib/bots/configSpec';

type Mode = 'manual' | TraderBotMode;

export function BotModeSwitch({ ticker, children }: { ticker: string | null; children: ReactNode }) {
  const botAccess = useBotAccess();
  const role = useAuthStore((s) => s.user?.role);
  // A bot-access trader OR an operator role (manager/admin) can drive the bot
  // flow from the dashboard — same gate as the Position-Tracker BotZones, so an
  // org-floating admin isn't locked out of the trader-native spawn surface.
  const canSpawn = botAccess || role === 'manager' || role === 'admin';
  const [mode, setMode] = useState<Mode>('manual');

  // No bot access/role or no Kalshi market → existing ticket, completely unchanged.
  if (!canSpawn || !ticker) return <>{children}</>;

  const modes: { value: Mode; label: string }[] = [
    { value: 'manual', label: 'Manual' },
    { value: 'vanilla', label: 'Vanilla' },
    { value: 'recycler', label: 'Recycler' },
  ];

  return (
    <div>
      <div className="border-border flex border-b">
        {modes.map((m) => (
          <button
            key={m.value}
            type="button"
            onClick={() => setMode(m.value)}
            className={cn(
              'flex-1 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wider transition-colors',
              mode === m.value
                ? 'text-foreground border-foreground/60 border-b-2'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {m.label}
          </button>
        ))}
      </div>
      {mode === 'manual' ? children : <BotSpawnForm key={mode} ticker={ticker} mode={mode} />}
    </div>
  );
}

function BotSpawnForm({ ticker, mode }: { ticker: string; mode: TraderBotMode }) {
  const personal = useAuthStore((s) => s.user?.botDefaults);
  const org = useAuthStore((s) => s.user?.orgBotDefaults);
  const kind = kindForMode(mode);
  const sizeField = sizeFieldForKind(kind);
  const spec = specForKind(kind);
  const resolved = useMemo(() => resolveBotConfig(kind, personal, org), [kind, personal, org]);

  const [override, setOverride] = useState<BotKindConfig>({});
  const config = useMemo(() => ({ ...resolved, ...override }), [resolved, override]);

  const allFields = useMemo(() => (spec?.sections ?? []).flatMap((s) => s.fields), [spec]);
  const builtinSize = allFields.find((f) => f.key === sizeField)?.builtin;
  const size = config[sizeField] ?? builtinSize ?? 1;
  // Advanced = non-strategy (non-orgOnly) fields, minus the size field shown above.
  const advFields = useMemo(
    () => (spec?.sections.filter((s) => !s.orgOnly) ?? []).flatMap((s) => s.fields).filter((f) => f.key !== sizeField),
    [spec, sizeField],
  );

  const [advanced, setAdvanced] = useState(false);
  // Two independent levers, each defaulted to the safe/sensible choice:
  //   ownership — org pool (default) vs claim to me
  //   mode      — guard / quote-for-rebate (default) vs let it ride / take fills
  const [claimMine, setClaimMine] = useState(false);
  const [ride, setRide] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<
    | null
    | { kind: 'ok' }
    | { kind: 'claimed' }
    | { kind: 'exists'; id?: string }
    | { kind: 'notConfigured' }
    | { kind: 'error'; msg?: string }
  >(null);

  const setField = (key: string, raw: string) => {
    setStatus(null);
    setConfirming(false);
    setOverride((o) => {
      const next = { ...o };
      if (raw.trim() === '') delete next[key];
      else {
        const n = Number(raw);
        if (Number.isFinite(n)) next[key] = n;
      }
      return next;
    });
  };

  const deploy = async () => {
    setBusy(true);
    setStatus(null);
    // Send the size the trader actually SEES (config value, else the kind's
    // built-in, else 1) explicitly, so the spawned bot can never run a different
    // size than the form showed — the recycler used to display 1 while the edge
    // ran its own default.
    const r = await spawnBot({
      ticker,
      kind,
      config: { ...config, [sizeField]: size },
      mode: ride ? 'cross_for_fills' : 'quote_for_rebate',
    });
    // Claim ONLY if the trader opted to take this bot personally. Default is the
    // org pool (unclaimed — anyone with access can claim it). Claiming is an
    // independent lever from let-it-ride; a claimed bot still starts in guard.
    if (r.ok && r.id && claimMine) await claimBot(r.id, ticker);
    setBusy(false);
    setConfirming(false);
    if (r.ok) setStatus({ kind: 'ok' });
    else if (r.code === 'BOT_EXISTS') setStatus({ kind: 'exists', id: r.existingId });
    else if (r.status === 503) setStatus({ kind: 'notConfigured' });
    else setStatus({ kind: 'error', msg: r.error });
  };

  const claimExisting = async (id: string) => {
    setBusy(true);
    const r = await claimBot(id, ticker);
    setBusy(false);
    setStatus(r.ok ? { kind: 'claimed' } : { kind: 'error', msg: r.error ?? 'Claim failed' });
  };

  return (
    <div className="space-y-3 p-3">
      <label className="block">
        <span className="text-muted-foreground text-[11px]">Contracts (per side)</span>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          value={config[sizeField] ?? ''}
          onChange={(e) => setField(sizeField, e.target.value)}
          placeholder={builtinSize !== undefined ? String(builtinSize) : '1'}
          className="bg-background border-border text-foreground focus:border-primary mt-1 w-full rounded border px-2 py-1.5 text-sm focus:outline-none"
        />
      </label>

      <ConfigSummary kind={kind} config={config} ride={ride} />

      {/* Two independent levers as segmented button-pairs (matches the trade
          ticket's BUY YES/NO + LIMIT/MARKET toggles). Defaults: org pool, guard. */}
      <div className="space-y-1.5">
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => setClaimMine(false)}
            className={cn(
              'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
              !claimMine ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground',
            )}
          >
            Org pool
          </button>
          <button
            type="button"
            onClick={() => setClaimMine(true)}
            className={cn(
              'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
              claimMine ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground',
            )}
          >
            Claim to me
          </button>
        </div>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => setRide(false)}
            className={cn(
              'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
              !ride ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground',
            )}
          >
            Guard
          </button>
          <button
            type="button"
            onClick={() => setRide(true)}
            className={cn(
              'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
              ride ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground hover:text-foreground',
            )}
          >
            Let it ride
          </button>
        </div>
      </div>

      {advFields.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setAdvanced((v) => !v)}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-[11px]"
          >
            <ChevronDown size={12} className={advanced ? '' : '-rotate-90'} /> Advanced
          </button>
          {advanced && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              {advFields.map((f) => (
                <label key={f.key} className="block" title={f.help}>
                  <span className="text-muted-foreground flex items-center gap-1 text-[10px]">
                    {f.label}
                    {f.unit ? <span className="opacity-60">{f.unit}</span> : null}
                  </span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={f.nonNegative ? 0 : undefined}
                    value={config[f.key] ?? ''}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder={f.builtin !== undefined ? String(f.builtin) : '—'}
                    className="bg-background border-border text-foreground focus:border-primary mt-0.5 w-full rounded border px-1.5 py-1 text-xs focus:outline-none"
                  />
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {!confirming ? (
        <button
          onClick={() => setConfirming(true)}
          disabled={busy}
          className="bg-primary text-primary-foreground flex w-full items-center justify-center gap-1.5 rounded px-3 py-2 text-sm font-medium disabled:opacity-50"
        >
          <Bot size={15} /> Deploy {mode === 'vanilla' ? 'Vanilla' : 'Recycler'} bot
        </button>
      ) : (
        <div className="space-y-2">
          <div className="text-muted-foreground text-[11px]">
            Deploy a {mode} bot at size {size},{' '}
            <span className={ride ? 'text-amber-400' : 'text-foreground'}>
              {ride ? 'let it ride (takes fills)' : 'guard mode (post-only)'}
            </span>
            , {claimMine ? 'claimed to you' : 'left in the org pool (anyone with access can claim it)'}?
          </div>
          <div className="flex gap-2">
            <button
              onClick={deploy}
              disabled={busy}
              className="bg-primary text-primary-foreground flex flex-1 items-center justify-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : null} Confirm deploy
            </button>
            <button
              onClick={() => setConfirming(false)}
              disabled={busy}
              className="border-border text-muted-foreground rounded border px-3 py-1.5 text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {status?.kind === 'ok' && (
        <div className="text-xs text-emerald-500">
          {claimMine
            ? 'Bot deployed + claimed — manage it in My Bots.'
            : 'Bot deployed to the org pool — claim it from Org bots to take the wheel.'}
        </div>
      )}
      {status?.kind === 'claimed' && (
        <div className="text-xs text-emerald-500">Claimed — it's in your Position Tracker.</div>
      )}
      {status?.kind === 'notConfigured' && (
        <div className="text-xs text-amber-400">Edge engine isn't configured for this lane.</div>
      )}
      {status?.kind === 'error' && (
        <div className="text-xs text-red-400">{status.msg ?? 'Deploy failed'}</div>
      )}
      {status?.kind === 'exists' && (
        <div className="bg-muted/40 rounded p-2 text-xs">
          <div className="text-muted-foreground mb-1.5">A {mode} bot is already running on this market.</div>
          {status.id && (
            <button
              onClick={() => claimExisting(status.id as string)}
              disabled={busy}
              className="bg-primary text-primary-foreground rounded px-2.5 py-1 text-xs font-medium disabled:opacity-50"
            >
              Claim it
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ConfigSummary({ kind, config, ride }: { kind: string; config: BotKindConfig; ride: boolean }) {
  const modeLabel = ride ? 'let it ride' : 'guard';
  if (kind === 'vanillamm') {
    const width = (config.belowCents ?? 1) + (config.aboveCents ?? 2);
    const avoid = ['proximityRatioPct', 'frontDistanceThreshold', 'velocityPullCentsPerSec', 'depthVelocityPullPerSec', 'polyMoveCents'].some(
      (k) => (config[k] ?? 0) > 0,
    );
    return (
      <div className="text-muted-foreground text-[10px]">
        {width}¢ wide · avoid-fill {avoid ? 'on' : 'off'} · {modeLabel}
      </div>
    );
  }
  return <div className="text-muted-foreground text-[10px]">queue presence · {modeLabel}</div>;
}
